import { AnimatePresence, motion } from 'framer-motion';
import {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDom from 'react-dom';
import { IconButton } from 'src/components/Buttons';
import { Content } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './ClickToExpand.css';

type ClickToExpandProps = {
  id: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  titleContentId: ContentId;
  trigger: React.ReactNode;
  content: React.ReactNode;
  location?: 'top' | 'center';
};

/**
 * There is no relative container for this component
 * It is important to make sure the root parent, where we want to show the expanded dialog,
 * has a relative position
 */
const ClickToExpandRaw = ({
  id,
  open,
  setOpen,
  titleContentId,
  trigger,
  content,
  location = 'center',
}: ClickToExpandProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<
    { top: number; left: number } | undefined
  >(undefined);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (triggerRef.current?.offsetParent && contentRef.current && open) {
        const buttonRect = triggerRef.current.getBoundingClientRect();
        const left =
          buttonRect.left +
          buttonRect.width / 2 -
          contentRef.current.offsetWidth / 2;
        const top =
          location === 'top'
            ? buttonRect.top +
              buttonRect.height -
              contentRef.current.offsetHeight -
              12
            : buttonRect.top +
              buttonRect.height / 2 -
              contentRef.current.offsetHeight / 2;

        setPosition({ top, left });
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [location, open]);

  const onClickGuardrail = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      setOpen(true);
    },
    [setOpen]
  );

  return (
    <>
      <div ref={triggerRef} onClick={onClickGuardrail}>
        {trigger}
      </div>

      <AnimatePresence key={id}>
        {open && (
          <Overlay onClose={() => setOpen(false)}>
            <motion.div
              ref={contentRef}
              onClick={(e) => e.stopPropagation()}
              className={styles.expandedDialog}
              style={{
                position: 'absolute',
                top:
                  position?.top ??
                  triggerRef.current!.getBoundingClientRect().top,
                left:
                  position?.left ??
                  triggerRef.current!.getBoundingClientRect().left,
                zIndex: 1000,
              }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <Stack direction="column" width="full" gap="m">
                <Stack
                  justifyContent="spaceBetween"
                  alignItems="center"
                  width="full"
                >
                  <div className={styles.dialogTitle}>
                    <Content id={titleContentId} />
                  </div>
                  <IconButton
                    icon={<CrossIcon />}
                    onClick={() => setOpen(false)}
                  />
                </Stack>
                {content}
              </Stack>
            </motion.div>
          </Overlay>
        )}
      </AnimatePresence>
    </>
  );
};

const Overlay = ({
  children,
  onClose,
}: PropsWithChildren & { onClose?: () => void }) => {
  // Create a DOM node dynamically
  const container = document.createElement('div');

  useEffect(() => {
    // Append to body when mounted
    document.body.appendChild(container);
    return () => {
      document.body.removeChild(container); // Clean up
    };
  }, [container]);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      onClose?.();
    },
    [onClose]
  );

  return ReactDom.createPortal(
    <div className={styles.overlay} onClick={onClick}>
      {children}
    </div>,
    container
  );
};

export const ClickToExpand = memo(ClickToExpandRaw);
